import React from 'react';
import Select from 'react-select';
import SelectChevron from './SelectChevron.jsx';
import { createScopedPortalHost } from '../utils/scopedPortal.js';

/**
 * The one donor multi-select (CON-11).
 *
 * Consolidated from two copies with identical props and identical logic whose
 * whole divergence was a react-select `styles` fork plus one load-bearing
 * difference: the widget's copy portalled its menu out of the scrolling
 * container and the checkout's did not, so the checkout's menu clipped.
 *
 * The portal is now unconditional, so `variant` selects appearance ONLY. CON-61
 * converged the presets where no distinct intent existed: both use the signed
 * donor control height, explicit transition, type sizes, token-backed muted
 * ink, chevron size and caller placeholder. See `decisions.md`
 * D-con-61-dropdown-preset.
 *
 * CON-155 closed the last axis CON-61 deferred. Under `decisions.md`
 * D-con-155-control-hover a donor INPUT is inert on hover and a donor BUTTON
 * reacts, so both presets now hold the control inert and both react on the
 * chip's remove target. What is left between them is ink family, type size and
 * the error border — no behavioural fork.
 *
 * Props (unchanged from both copies):
 * - options        string[]   available options
 * - selectedValues string[]   current selection
 * - onChange       (string[]) => void
 * - placeholder    string     rendered by both presets
 * - error          boolean    error border
 * - brandColors    object     accepted by both copies, read by neither
 * - ariaLabel      string     the control's accessible name (the question
 *                             title); A11-003, a combobox needs a name
 * - ariaErrorMessage string   id of the element holding this field's error.
 *                             With `error`, the input also reports
 *                             `aria-invalid`, which is what a blocked submit's
 *                             focus move looks for (A11-003). react-select
 *                             owns `aria-describedby` on its input, so the
 *                             error is referenced through `aria-errormessage`
 * - variant        'elements' | 'checkout'
 */

/**
 * Donor redesign (CON-515, SPEC §C Inputs/Focus): the field recipe, spelled as
 * a react-select style object because react-select styles through JS. Radius
 * 12, white, 1px edge; hover = strong edge (Paper 66D-0 overrides the older
 * D-con-155 "inputs are inert on hover"); focus = accent border + 1px inset
 * accent; error = the error edge, which does not react to hover.
 */
const fieldControlStyle = (error, isFocused) => {
  const rest = error ? 'var(--cs-error-color)' : 'var(--cs-border-control)';
  const border = isFocused ? 'var(--cs-input-focus-ring)' : rest;
  return {
    minHeight: 'var(--cs-input-height)',
    border: `1px solid ${border}`,
    borderRadius: 'var(--cs-input-radius)',
    backgroundColor: 'var(--cs-surface-0)',
    boxShadow: isFocused ? 'var(--cs-input-focus-shadow)' : 'var(--cs-control-shadow)',
    '&:hover': {
      borderColor: isFocused || error ? border : 'var(--cs-border-control-hover)'
    },
  };
};

const VARIANTS = {
  elements: (error) => {
    return {
    control: (provided, state) => ({
      ...provided,
      ...fieldControlStyle(error, state.isFocused),
      height: 'auto',
      // Match react-select's former computed default explicitly so a vendor
      // change cannot silently fork the presets again.
      transition: 'all 100ms', // cs-allow-hardcoded: converge on checkout's prior react-select transition
      cursor: 'pointer'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'var(--cs-text-primary)',
      fontSize: '14px',
      padding: '2px 4px'
    }),
    // CON-155. The remove target is the donor product's smallest button, and
    // this ground is its only pointer affordance — so it reacts, and the
    // `checkout` preset converged onto these values rather than the other way
    // round. D-con-155-control-hover.
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'var(--cs-text-tertiary)',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'var(--cs-surface-2)',
        color: 'var(--cs-text-primary)'
      }
    }),
    // CON-59. The same cursor treatment as the `checkout` preset, for the same
    // reason. This preset did change ground on `isFocused`, so the defect read
    // as "the widget is fine" — but that ground step is one this file's own
    // token set records as imperceptible, so a keyboard donor on an embedded
    // form had a cursor in name only. The bar is the signal; the transparent
    // outline is what is left of it under forced colors. See the `checkout`
    // preset below for the full reasoning — the two are deliberately identical
    // here, and only here.
    //
    // The selected arm is unreachable: `isMulti` with no
    // `hideSelectedOptions` makes react-select filter chosen options out of the
    // list. CON-61 therefore collapsed it onto the same focused/rest treatment
    // the checkout preset already uses.
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? 'var(--cs-surface-2)'
        : 'var(--cs-surface-0)',
      boxShadow: state.isFocused ? 'inset 3px 0 0 0 var(--cs-text-primary)' : 'none',
      outline: state.isFocused ? '2px solid transparent' : 'none',
      outlineOffset: '-2px',
      color: 'var(--cs-text-primary)',
      cursor: 'pointer',
      fontSize: '14px',
      padding: '10px 12px',
      '&:active': {
        backgroundColor: 'var(--cs-surface-2)'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--cs-text-tertiary)',
      fontSize: '1rem'
    }),
    input: (provided) => ({
      ...provided,
      fontSize: '1rem',
      color: 'var(--cs-text-primary)'
    })
    // NO `dropdownIndicator` ENTRY, IN EITHER PRESET — see the `DropdownIndicator`
    // component below. CON-155 deleted both, plus the app-level `!important` rule
    // in the checkout's styles.css that chased the same element, after measuring
    // that none of the three could ever apply.
    };
  },

  // React-select merges these with its default theme, which carries `&:hover`
  // on the control. That hover is set back to its rest value on purpose: under
  // D-con-155-control-hover a donor input is inert on hover, and this control is
  // an input. The remove target below is the exception the rule names, and it
  // reacts.
  checkout: (error) => {
    return {
      control: (provided, state) => ({
        ...provided,
        ...fieldControlStyle(error, state.isFocused),
        transition: 'all 100ms', // cs-allow-hardcoded: preserve checkout's prior react-select transition explicitly
        cursor: 'pointer',
      }),
      multiValueLabel: (provided) => ({
        ...provided,
        color: 'var(--cs-text-primary)',
        fontSize: '14px',
        fontFamily: 'var(--cs-font-sans)',
        padding: '2px 4px'
      }),
      // CON-155. Converged onto the `elements` values. This used to pin both
      // properties back to rest, and an app-level `!important` rule in the
      // checkout's styles.css held it there a second time; both are gone.
      // D-con-155-control-hover puts the remove target on the BUTTON side of the
      // rule: it is a ~16px hit area whose only pointer affordance is this
      // ground, so inertness here bought consistency by removing the signal.
      multiValueRemove: (provided) => ({
        ...provided,
        color: 'var(--cs-text-tertiary)',
        cursor: 'pointer',
        ':hover': {
          backgroundColor: 'var(--cs-surface-2)',
          color: 'var(--cs-text-primary)'
        }
      }),
      // CON-59. This preset used to take neither `isFocused` nor `isSelected`,
      // so the option the keyboard was on rendered identically to every other
      // row and a donor without a mouse had no affordance at all (WCAG 2.4.7).
      //
      // THE BAR IS THE SIGNAL. A leading-edge rule in the option's own ink is a
      // shape that appears, and it clears the non-text contrast floor against
      // either ground with room to spare. The ground change beside it is NOT a
      // second signal and must not be argued as one — this file's own token set
      // records that step as a difference no donor can see — it is there so the
      // two presets agree on the cursor's ground.
      //
      // `--cs-input-focus-shadow`, the named donor focus recipe, is deliberately
      // NOT read here on two counts: it is an OUTER halo, which on a full-bleed
      // menu row is clipped and overlaps its neighbours, and design.md 6.9
      // records its alpha as a knowingly sub-floor interim. The bar clears the
      // contrast floor that recipe does not; it does not claim to satisfy the
      // AAA focus-appearance AREA rule, which a perimeter would.
      //
      // A THIRD signal, invisible until it is the only one left. Forced-colors
      // mode (Windows High Contrast) drops box-shadow entirely AND repaints
      // every background from the user's palette, so the two signals above both
      // disappear there and the cursor would be lost again. An outline survives
      // that mode, and a TRANSPARENT one is forced to a system colour when it
      // does — so this paints nothing in an ordinary browser and becomes the
      // whole indicator in a forced palette. The negative offset keeps it inside
      // the row rather than under the menu's edge.
      //
      // `:active` takes the cursor's ground rather than the rest ground, because
      // the row a pointer is pressing is by definition the row the cursor is on:
      // pressing an option should change nothing, not flash. It has to be
      // written rather than left out — react-select's own theme paints that
      // state a solid blue, and the app rule that used to mask it is the one
      // this issue had to narrow.
      //
      // `isSelected` falls through to the rest ground. Today the state cannot
      // render at all: `isMulti` with no `hideSelectedOptions` makes react-select
      // filter chosen options out of the list, and the chosen ones show in the
      // control as removable chips instead. The pin is for the caller who one day
      // passes `hideSelectedOptions={false}` and would otherwise get the same
      // vendor blue.
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? 'var(--cs-surface-2)' : 'var(--cs-surface-0)',
        boxShadow: state.isFocused ? 'inset 3px 0 0 0 var(--cs-text-primary)' : 'none',
        outline: state.isFocused ? '2px solid transparent' : 'none',
        outlineOffset: '-2px',
        color: 'var(--cs-text-primary)',
        cursor: 'pointer',
        fontFamily: 'var(--cs-font-sans)',
        fontSize: '14px',
        padding: '10px 12px',
        '&:active': {
          backgroundColor: 'var(--cs-surface-2)'
        }
      }),
      placeholder: (provided) => ({
        ...provided,
        color: 'var(--cs-text-tertiary)',
        fontFamily: 'var(--cs-font-sans)',
        fontSize: '16px'
      }),
      input: (provided) => ({
        ...provided,
        fontFamily: 'var(--cs-font-sans)',
        fontSize: '16px',
        color: 'var(--cs-text-primary)'
      })
      // No `dropdownIndicator` entry — see the `elements` preset's note and the
      // `DropdownIndicator` component below.
    };
  }
};

// Identical in both copies.
const SHARED_STYLES = {
  valueContainer: (provided) => ({
    ...provided,
    padding: '4px 10px',
    gap: '4px'
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: 'var(--cs-surface-1)',
    borderRadius: 'var(--cs-checkbox-radius)',
    padding: '2px 4px',
    border: '1px solid var(--cs-border-control)'
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: 'var(--cs-menu-radius)',
    marginTop: '4px',
    boxShadow: 'var(--cs-menu-shadow)',
    zIndex: 50
  }),
  // CON-59. Two independent things keep a body-portalled menu usable, and this
  // component had neither. Both are measured, not reasoned.
  //
  // PAINT ORDER. The portal host is a bare `<body>` child with no stacking
  // context of its own, so what the menu competes with the page on is the PORTAL
  // WRAPPER's z-index — not the `menu` z-index above, which only orders things
  // inside the wrapper. react-select's default leaves the wrapper under any
  // raised layer, and both consumers have one over the menu's ground: the
  // checkout's drawer and the widget's own page layer. `--cs-z-menu` is the
  // ladder step a menu belongs on, above both.
  //
  // HIT TESTING, which paint order does NOT buy. Every drawer this menu opens
  // inside is a Radix dialog underneath, and vaul does not forward its own
  // `modal` prop to it — so Radix is always modal and always turns the body
  // inert (`pointer-events: none`), whatever the drawer was configured with.
  // The dialog's own subtree is exempted; a body-level portal is not in it, and
  // `pointer-events` inherits. So the wrapper claims it back for its own
  // subtree, the same way this repo already does for Radix's popper wrapper.
  //
  // Without it, MEASURED: the menu paints and every click and tap falls through
  // on the widget's mobile drawer and on the checkout's SECOND open in a page
  // (vaul restores the body once per drawer mount; Radix re-inerts it on every
  // one). The keyboard still works throughout — this is the pointer and touch
  // route to a REQUIRED question on the payment path, not the only route.
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 'var(--cs-z-menu)',
    pointerEvents: 'auto'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  })
};

// The typed text, placeholder and chips start where a native donor select's text does: the
// field's inline inset inside the border, with react-select's own 2px text margins removed.
const FIELD_INSET_STYLES = {
  valueContainer: (provided) => ({
    ...provided,
    paddingLeft: 'var(--cs-input-padding-x)',
  }),
  placeholder: (provided) => ({ ...provided, marginLeft: 0 }),
  input: (provided) => ({ ...provided, marginLeft: 0 }),
};

export default function MultiSelectDropdown({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = 'Select options',
  error = false,
  // eslint-disable-next-line no-unused-vars
  brandColors = {},
  variant = 'elements',
  ariaLabel,
  ariaErrorMessage,
}) {
  // The portal host exists only while the menu is open. Hosted-page keeps its
  // mobile and desktop forms mounted together and hides one with CSS; an eager
  // host for the hidden copy would otherwise remain under <body> while the
  // mobile drawer truthfully advertises aria-modal. It is anchored on the
  // dropdown indicator — a node this file already renders, inside the control
  // — rather than on a wrapper div, because adding an element around <Select>
  // would be a structural change to every call site's layout.
  const [menuHost, setMenuHost] = React.useState(null);
  const menuHostRef = React.useRef(null);
  const anchorRef = React.useRef(null);

  const anchorIndicator = React.useCallback((node) => {
    if (node) anchorRef.current = node;
  }, []);

  // Tracked apart from `menuHost`: the host is skipped when the anchor is not
  // mounted yet, and the menu is open all the same.
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const handleMenuOpen = React.useCallback(() => {
    setIsMenuOpen(true);
    if (menuHostRef.current || !anchorRef.current) return;
    const host = createScopedPortalHost(anchorRef.current);
    menuHostRef.current = host;
    setMenuHost(host);
  }, []);

  const handleMenuClose = React.useCallback(() => {
    setIsMenuOpen(false);
    menuHostRef.current?.remove();
    menuHostRef.current = null;
    setMenuHost(null);
  }, []);

  // CON-373. Consume the menu's Escape before Radix handles drawer dismissal —
  // the same guard `FeeInfoTooltip` keeps for its panel. Every drawer this menu
  // opens in is a Radix DismissableLayer, which hears Escape on the DOCUMENT in
  // the CAPTURE phase, before react-select's handler on the input, and
  // dismisses unless the event is already `defaultPrevented`. Without this, a
  // donor closing the list closed the whole checkout and lost their entries.
  //
  // A WINDOW capture listener runs ahead of that document one. It only
  // preventDefaults — deliberately NOT stopPropagation, unlike the tooltip:
  // react-select still has to receive the key to close its own menu, clear its
  // search text and keep focus on the control, and Radix decides on
  // `defaultPrevented` alone. It is attached only while the menu is open, so
  // with the menu closed the next Escape reaches the drawer exactly as before.
  React.useEffect(() => {
    if (!isMenuOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key !== 'Escape' || event.isComposing) return;
      event.preventDefault();
    };
    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [isMenuOpen]);

  React.useEffect(
    () => () => {
      menuHostRef.current?.remove();
      menuHostRef.current = null;
    },
    [],
  );

  const selectOptions = options.map((option) => ({ value: option, label: option }));

  // Empty strings would otherwise render as empty tags.
  const selectValue = selectedValues
    .filter((value) => value && value.trim() !== '')
    .map((value) => ({ value, label: value }));

  const baseStyles = { ...SHARED_STYLES, ...VARIANTS[variant](error) };
  // The inset styles layer over the preset's own entry for the same part, never replace it.
  const customStyles = { ...baseStyles };
  for (const [part, inset] of Object.entries(FIELD_INSET_STYLES)) {
    const own = baseStyles[part];
    customStyles[part] = (provided, state) => inset(own ? own(provided, state) : provided, state);
  }

  // NOTHING IN EITHER PRESET REACHES THIS COMPONENT. It replaces react-select's
  // own indicator and spreads only `props.innerProps`, so it never receives the
  // generated `multi-select__dropdown-indicator` class and never calls
  // `getStyles` — which means a `dropdownIndicator` entry in a preset, and any
  // app rule written against that class name, are both dead letters. CON-155
  // measured all three that existed (two preset `:hover` colours and an
  // `!important` rule in the checkout's styles.css) and deleted them; the chevron
  // is inert in both apps, which is what D-con-155-control-hover wants anyway.
  // Its colour comes from the Tailwind class on the icon and its box from the
  // inline style here. If this component is ever removed, restore a
  // `dropdownIndicator` entry deliberately — react-select's vendor default
  // carries its own `:hover`.
  const DropdownIndicator = (props) => (
    <div
      {...props.innerProps}
      ref={anchorIndicator}
      // The chevron's right edge on the field's inline inset, like every native donor select
      // (SELECT_CHEVRON_FIELD_CLASS): the inset less the control's own border.
      style={{
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 'var(--cs-space-2)',
        paddingRight: 'calc(var(--cs-input-padding-x) - var(--cs-border-width))',
      }}
    >
      <SelectChevron />
    </div>
  );

  const handleChange = (selected) => {
    onChange((selected || []).map((option) => option.value));
  };

  return (
    <Select
      isMulti
      options={selectOptions}
      value={selectValue}
      onChange={handleChange}
      placeholder={placeholder}
      styles={customStyles}
      components={{ DropdownIndicator }}
      closeMenuOnSelect={false}
      isClearable={false}
      classNamePrefix="multi-select"
      aria-label={ariaLabel}
      aria-invalid={error ? true : undefined}
      aria-errormessage={error ? ariaErrorMessage : undefined}
      menuPortalTarget={menuHost}
      menuPosition="fixed"
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
    />
  );
}
